import React, {
  useState,
  useEffect,
  useRef,
  cloneElement,
  Children,
  useCallback,
} from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { debounceSearch } from '../../common/tools';

const AutocompleteWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }

  &.active {
    background-color: #e6f7ff;
  }
`;

const MainText = styled.span`
  display: block;
  color: #333;
  font-weight: 500;
`;

const SecondaryText = styled.span`
  display: block;
  color: #666;
  font-size: 0.875rem;
  margin-top: 2px;
`;

function GooglePlacesAutocomplete({ children, onPlaceChanged, bounds }) {
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    // Verificar si Google Maps está disponible
    if (window.google && window.google.maps) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Función para buscar predicciones
  const searchPredictions = useCallback(
    (value) => {
      if (!value.trim()) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      if (autocompleteServiceRef.current) {
        const request = {
          input: value,
          ...(bounds && { bounds }),
        };

        autocompleteServiceRef.current.getPlacePredictions(
          request,
          (predictions, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setSuggestions(predictions);
              setShowSuggestions(true);
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }
          }
        );
      }
    },
    [bounds]
  );

  // Crear versión debounced de la búsqueda
  const debouncedSearch = useCallback(debounceSearch(searchPredictions, 300), [
    searchPredictions,
  ]);

  const handleInputChange = (value) => {
    setInputValue(value);
    setSelectedIndex(-1);
    debouncedSearch(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);

    if (placesServiceRef.current && onPlaceChanged) {
      placesServiceRef.current.getDetails(
        { placeId: suggestion.place_id },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            onPlaceChanged(place);
          }
        }
      );
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Clonar el input hijo y agregar los event handlers
  const childElement = Children.only(children);
  const enhancedChild = cloneElement(childElement, {
    value: inputValue,
    onChange: (e) => {
      handleInputChange(e.target.value);
      if (childElement.props.onChange) {
        childElement.props.onChange(e);
      }
    },
    onKeyDown: (e) => {
      handleKeyDown(e);
      if (childElement.props.onKeyDown) {
        childElement.props.onKeyDown(e);
      }
    },
    onFocus: (e) => {
      if (suggestions.length > 0) {
        setShowSuggestions(true);
      }
      if (childElement.props.onFocus) {
        childElement.props.onFocus(e);
      }
    },
  });

  return (
    <AutocompleteWrapper ref={wrapperRef}>
      <>
        {enhancedChild}

        {showSuggestions && suggestions.length > 0 ? (
          <SuggestionsList>
            {suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={suggestion.place_id}
                className={index === selectedIndex ? 'active' : ''}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <MainText>
                  {suggestion.structured_formatting.main_text}
                </MainText>
                <SecondaryText>
                  {suggestion.structured_formatting.secondary_text}
                </SecondaryText>
              </SuggestionItem>
            ))}
          </SuggestionsList>
        ) : (
          <></>
        )}
      </>
    </AutocompleteWrapper>
  );
}

GooglePlacesAutocomplete.propTypes = {
  onPlaceChanged: PropTypes.func,
  bounds: PropTypes.object,
  children: PropTypes.object,
};

export default GooglePlacesAutocomplete;
